import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Trophy, Medal, Award, Crown, Star, GraduationCap, CheckCircle2
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_STYLES = {
  1: { icon: Crown, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-500/30' },
  2: { icon: Medal, color: 'text-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/20', border: 'border-slate-400/30' },
  3: { icon: Medal, color: 'text-amber-700', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-500/30' },
};

export default function Leaderboard() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/achievements/leaderboard`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  const board = data?.leaderboard || [];

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
          <Trophy className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="leaderboard-heading">Leaderboard</h1>
          <p className="text-sm text-muted-foreground">See how your team is progressing through CountBucks</p>
        </div>
      </div>

      {/* Top 3 Podium */}
      {board.length >= 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {board.slice(0, 3).map((entry) => {
            const rankStyle = RANK_STYLES[entry.rank] || {};
            const RankIcon = rankStyle.icon || Star;
            return (
              <Card key={entry.user_id} className={cn("transition-all", rankStyle.border)} data-testid={`podium-${entry.rank}`}>
                <CardContent className="pt-5 text-center space-y-3">
                  <div className="flex justify-center">
                    <div className="relative">
                      <Avatar className="w-16 h-16 border-2" style={{ borderColor: entry.rank <= 3 ? undefined : 'transparent' }}>
                        <AvatarImage src={`${API_URL}/api/users/${entry.user_id}/avatar`} alt={entry.name} />
                        <AvatarFallback className={cn("text-lg font-bold", rankStyle.bg)}>
                          {entry.name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className={cn("absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 border-background", rankStyle.bg)}>
                        <RankIcon className={cn("w-3.5 h-3.5", rankStyle.color)} />
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold">{entry.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{entry.role}</p>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: rankStyle.color?.replace('text-', '') ? undefined : '#f59e0b' }}>{entry.badges_earned}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Badges</p>
                    </div>
                    <div className="w-px h-8 bg-border" />
                    <div className="text-center">
                      <p className="text-2xl font-bold text-indigo-500">{entry.university_modules}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Modules</p>
                    </div>
                  </div>
                  {entry.certified && (
                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
                      <Award className="w-3 h-3" /> Certified
                    </Badge>
                  )}
                  {entry.latest_badge && (
                    <p className="text-[10px] text-muted-foreground">Latest: {entry.latest_badge}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Full Rankings Table */}
      {board.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Full Rankings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead className="text-center">Badges</TableHead>
                  <TableHead className="text-center">Modules</TableHead>
                  <TableHead className="text-center">Certified</TableHead>
                  <TableHead>Latest Badge</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {board.map((entry) => {
                  const rankStyle = RANK_STYLES[entry.rank];
                  return (
                    <TableRow key={entry.user_id} data-testid={`rank-row-${entry.rank}`}>
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {rankStyle ? (
                            <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", rankStyle.bg)}>
                              {React.createElement(rankStyle.icon, { className: cn("w-4 h-4", rankStyle.color) })}
                            </div>
                          ) : (
                            <span className="text-sm font-mono text-muted-foreground font-bold">{entry.rank}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <Avatar className="w-7 h-7">
                            <AvatarImage src={`${API_URL}/api/users/${entry.user_id}/avatar`} alt={entry.name} />
                            <AvatarFallback className="text-[10px] font-bold">{entry.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{entry.name}</p>
                            <p className="text-[10px] text-muted-foreground capitalize">{entry.role}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold">{entry.badges_earned}</span>
                        <span className="text-muted-foreground text-xs">/{entry.total_badges}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold">{entry.university_modules}</span>
                        <span className="text-muted-foreground text-xs">/8</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {entry.certified ? (
                          <CheckCircle2 className="w-4 h-4 text-amber-500 mx-auto" />
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.latest_badge ? (
                          <span className="text-xs text-muted-foreground">{entry.latest_badge}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No badges yet</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {board.length === 0 && (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Trophy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No team members with badges enabled yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Start earning badges to appear on the leaderboard!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
